import { api } from "./client";

// The email_log router is shared with nmms and lives under the same
// /near_miss prefix the axios client already carries, so the paths here
// are relative to it.

export interface EmailRecipientDetail {
  email: string;
  name?: string | null;
}

/**
 * One delivery attempt from the backend `email_log` table. `recipients`
 * is where the mail actually went — in non-prod every send is redirected
 * to a notifications inbox — while `recipient_details` records the
 * audience the backend intended, with real names. Rows written before
 * that column existed omit it (see utils/recipientDisplay).
 */
export interface EmailDelivery {
  id: string;
  entity_type: string;
  entity_id: string;
  event: string;
  subject: string;
  recipients: string[];
  recipient_details?: EmailRecipientDetail[] | null;
  status: string;
  error?: string | null;
  created_at: string;
}

export interface EmailContent {
  id: string;
  subject: string;
  html: string;
}

/** Delivery attempts for one entity, newest first (backend ordering). */
export async function listEmailDeliveries(
  entityType: string,
  entityId: string,
): Promise<EmailDelivery[]> {
  const { data } = await api.get<EmailDelivery[]>("/emails", {
    params: { entity_type: entityType, entity_id: entityId },
  });
  return data;
}

/** Re-fire a recorded email. Returns the fresh attempt row. */
export async function resendEmail(emailId: string): Promise<EmailDelivery> {
  const { data } = await api.post<EmailDelivery>(`/emails/${emailId}/resend`);
  return data;
}

/** The stored body, fetched on demand — it's large and rarely opened. */
export async function getEmailContent(
  emailId: string,
): Promise<EmailContent> {
  const { data } = await api.get<EmailContent>(`/emails/${emailId}/content`);
  return data;
}
